"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  // CarouselNext,
  // CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";

interface BannerItem {
  href: string;
  label: string;
  description?: string;
  /** <p> 라벨 폰트 클래스 (카드별로 상이) */
  labelClass: string;
  src: string;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
  imgStyle?: React.CSSProperties;
}

const BANNER_ITEMS: BannerItem[] = [
  {
    href: "/theory/theory-expected-questions",
    label: "3회 이론 예상",
    labelClass: "text-center text-xl md:text-2xl font-semibold mt-1 md:mt-3",
    src: "/main/compressed_gamja.webp",
    alt: "2026년 3회 정보처리기사 실기 이론 예상",
    width: 150,
    height: 150,
    priority: true,
  },
  {
    href: "/coding",
    label: "3회 코딩 예상",
    labelClass: "text-center text-xl md:text-2xl font-semibold mt-1 md:mt-3",
    src: "/coding/c-language/c-language_gamja.webp",
    alt: "2026년 3회 정보처리기사 실기 코딩 예상",
    width: 150,
    height: 150,
    priority: true,
  },
  {
    href: "/summary-pdf/26-3-compressed",
    label: "3회 이론 압축 PDF",
    labelClass: "text-center text-xl md:text-xl font-semibold mt-1 md:mt-3",
    src: "/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp",
    alt: "2026년 3회 실기 이론 압축 PDF",
    width: 150,
    height: 150,
    priority: true,
  },
  {
    href: "/summary-pdf/26-3-coding-compressed",
    label: "3회 코딩 압축 PDF",
    labelClass: "text-center text-xl md:text-xl font-semibold mt-1 md:mt-3",
    src: "/summary-pdf/26-1-coding-compressed/coding_compressed_gamja.webp",
    alt: "2026년 3회 실기 코딩 압축 PDF",
    width: 150,
    height: 150,
  },
  {
    href: "/theory/4week-study-plan",
    label: "이론 4주 공부계획",
    labelClass: "text-center text-xl md:text-2xl font-semibold mt-1 md:mt-3",
    src: "/theory/contents/4week-study-plan/detective_gamja_1.webp",
    alt: "2026년 3회 정보처리기사 실기 이론 4주 공부계획",
    width: 130,
    height: 130,
    priority: true,
  },
  {
    href: "/coding/coding-study-plan",
    label: "코딩 6주 공부계획",
    labelClass: "text-center text-xl md:text-2xl font-semibold mt-1 md:mt-3",
    src: "/main/dev_gamza.webp",
    alt: "2026년 3회 정보처리기사 실기 코딩 6주 공부계획",
    width: 150,
    height: 150,
  },
  {
    href: "/exam-registration/jeongcheogi-pass-rate",
    label: "합격률",
    labelClass: "text-xl md:text-4xl font-semibold md:mt-1",
    src: "/main/happy_gamza.webp",
    alt: "합격률",
    width: 150,
    height: 150,
  },
];

const MOBILE_HOME_BANNER_ITEMS: BannerItem[] = [
  {
    href: "/theory/theory-expected-questions",
    label: "압축공부법",
    description: "출제 경향 기준으로 먼저 볼 개념만 빠르게",
    labelClass: "text-lg font-black text-foreground",
    src: "/theory/contents/theory-expected-questions/compressed_gamja.webp",
    alt: "이론 압축 공부법",
    width: 112,
    height: 112,
    priority: true,
  },
  {
    href: "/theory/4week-study-plan",
    label: "공부계획",
    description: "이론 4주부터 코딩 6주까지 학습 순서 잡기",
    labelClass: "text-lg font-black text-foreground",
    src: "/theory/contents/4week-study-plan/detective_gamja_1.webp",
    alt: "4주 공부계획",
    width: 112,
    height: 112,
    priority: true,
    imgStyle: { width: "auto", height: "auto" },
  },
  {
    href: "/summary-pdf",
    label: "압축 PDF",
    description: "회차별 요약 PDF와 구매 가능한 자료 모아보기",
    labelClass: "text-lg font-black text-foreground",
    src: "/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp",
    alt: "정보처리기사 실기 압축 PDF",
    width: 112,
    height: 112,
    priority: true,
  },
];

/** 카드 한 장 — 캐루셀/스택 공용 내부 마크업. */
function BannerCard({ item }: { item: BannerItem }) {
  return (
    <Link
      href={item.href}
      className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="md:p-1">
        <Card>
          <CardContent className="flex flex-col items-center justify-center p-2 md:p-6">
            <p className={item.labelClass}>{item.label}</p>
            <Image
              src={item.src}
              alt={item.alt}
              width={item.width}
              height={item.height}
              priority={item.priority}
              className="object-contain"
              style={{ width: item.width, height: item.height }}
            />
          </CardContent>
        </Card>
      </div>
    </Link>
  );
}

function MobileHomeBannerCard({ item }: { item: BannerItem }) {
  return (
    <Link
      href={item.href}
      className="group block h-full rounded-2xl border border-amber-200 bg-card p-3 shadow-sm hover:border-amber-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 motion-safe:transition-[transform,border-color,box-shadow] motion-safe:hover:-translate-y-0.5 dark:border-amber-900 dark:hover:border-amber-700"
    >
      <div className="flex min-h-[112px] items-center gap-3">
        <div className="min-w-0 flex-1">
          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
            합격 도우미
          </span>
          <p className="mt-2 text-lg font-black leading-tight text-foreground">
            {item.label}
          </p>
          <p className="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground">
            {item.description}
          </p>
        </div>
        <Image
          src={item.src}
          alt={item.alt}
          width={item.width}
          height={item.height}
          priority={item.priority}
          style={item.imgStyle}
          className="h-20 w-20 shrink-0 object-contain transition-transform group-hover:scale-105"
        />
      </div>
    </Link>
  );
}

/**
 * 컴팩트 카드 한 장 — 스택 모드 전용. 시험장 찾기 좌측 카드(원서접수 일정)와 동일한
 * 높이·아이콘·텍스트 크기(h-13 행 · 40px 아이콘 · text-base)로 노출한다.
 *
 * shadcn Card 는 기본 `py-6 gap-6` 라 내부 높이를 강제해도 카드가 커진다. 좌측 InfoCards
 * (h-13 Button)와 높이를 맞추기 위해 Card 대신 평면 div 로 카드 룩(border/rounded/shadow)을 직접 구성.
 */
function BannerCompactCard({ item }: { item: BannerItem }) {
  return (
    <Link
      href={item.href}
      className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="flex h-13 w-full items-center gap-3 rounded-xl border bg-card px-3 shadow-sm transition-colors hover:border-amber-300 dark:hover:border-amber-700">
        <Image
          src={item.src}
          alt={item.alt}
          width={40}
          height={40}
          priority={item.priority}
          className="h-10 w-10 shrink-0 object-contain"
        />
        <span className="text-wrap text-base font-semibold text-foreground">
          {item.label}
        </span>
      </div>
    </Link>
  );
}

/**
 * 합격 도우미 배너.
 * stacked=true(JCG-2100 셸 우측 사이드바): 캐루셀 없이 컴팩트 카드(h-13 행)를 세로로 쌓아 노출.
 * variant="mobile-home"(JCG-2215): 모바일 홈 전용 3개 항목 + 클라이언트 랜덤 시작 카드.
 * false(기본): 기존 가로 autoplay 캐루셀(2~3열).
 */
export default function Banner({
  stacked = false,
  variant = "default",
}: {
  stacked?: boolean;
  variant?: "default" | "mobile-home";
}) {
  const [api, setApi] = React.useState<CarouselApi>();
  const isMobileHome = variant === "mobile-home";
  const items = isMobileHome ? MOBILE_HOME_BANNER_ITEMS : BANNER_ITEMS;

  React.useEffect(() => {
    if (!api || !isMobileHome) return;
    api.scrollTo(Math.floor(Math.random() * MOBILE_HOME_BANNER_ITEMS.length), true);
  }, [api, isMobileHome]);

  if (stacked) {
    return (
      <div className="flex flex-col gap-2">
        {BANNER_ITEMS.map((item) => (
          <BannerCompactCard key={item.href} item={item} />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("overflow-hidden", isMobileHome ? "px-4 py-3" : "p-4")}>
      <Carousel
        setApi={isMobileHome ? setApi : undefined}
        aria-label={isMobileHome ? "모바일 홈 학습 배너" : "합격 도우미 배너"}
        plugins={isMobileHome ? undefined : [Autoplay({ delay: 3500 })]}
        opts={{
          align: "start",
          loop: true,
        }}
      >
        <CarouselContent className={isMobileHome ? "-ml-3" : undefined}>
          {items.map((item) => (
            <CarouselItem
              key={item.href}
              className={cn(
                isMobileHome
                  ? "basis-[88%] pl-3 min-[380px]:basis-[82%]"
                  : "basis-1/2 md:basis-1/3 lg:basis-1/3",
              )}
            >
              {isMobileHome ? (
                <MobileHomeBannerCard item={item} />
              ) : (
                <BannerCard item={item} />
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
        {/* <CarouselPrevious />
        <CarouselNext /> */}
      </Carousel>
    </div>
  );
}
